import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import ThemeToggle from '@/components/ThemeToggle';
import { Zap, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(loginData.email, loginData.password);
      toast.success('Welcome back!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Check the email and password, then try again.');
      toast.error('Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen" data-testid="login-page">
      <header className="absolute inset-x-0 top-0 z-20 flex items-center justify-between gap-3 p-4 sm:p-6">
        <div className="glass flex items-center gap-3 px-3 py-2">
          <div className="grid h-10 w-10 place-items-center rounded-2xl bg-primary text-primary-foreground">
            <Zap className="h-5 w-5" aria-hidden="true" />
          </div>
          <span className="font-heading text-lg font-bold tracking-tight">Vdrive</span>
        </div>
        <div className="glass rounded-full p-1">
          <ThemeToggle testId="theme-toggle" />
        </div>
      </header>

      <div className="grid min-h-screen lg:grid-cols-2">
        <section className="relative hidden items-end p-10 lg:flex xl:p-16">
          <div className="glass-strong max-w-xl p-10 text-foreground">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              OCPP 1.6J central system
            </p>
            <h1 className="mt-4 font-heading text-4xl font-bold leading-tight xl:text-5xl">
              EV charging management, in clear view.
            </h1>
            <p className="mt-4 max-w-md text-base leading-relaxed text-muted-foreground">
              Monitor sessions, chargers, and alarms with the same live picture your operators use on site.
            </p>
            <dl className="mt-8 grid grid-cols-2 gap-4 text-sm">
              <div className="glass-subtle p-4">
                <dt className="text-muted-foreground">Monitoring</dt>
                <dd className="mt-1 font-mono text-lg font-semibold text-foreground">Real-time</dd>
              </div>
              <div className="glass-subtle p-4">
                <dt className="text-muted-foreground">Protocol</dt>
                <dd className="mt-1 font-mono text-lg font-semibold text-foreground">OCPP 1.6J</dd>
              </div>
            </dl>
          </div>
        </section>

        <section className="flex items-center justify-center px-4 pb-10 pt-28 sm:px-8">
          <Card className="glass-strong w-full max-w-md">
            <CardHeader className="space-y-2 text-left">
              <CardTitle className="font-heading text-2xl">Welcome back</CardTitle>
              <CardDescription>Sign in to the charging network.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4" noValidate>
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    autoComplete="username"
                    inputMode="email"
                    placeholder="admin@vdrive.com"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    data-testid="login-email"
                    aria-invalid={error ? 'true' : 'false'}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      className="pr-12"
                      data-testid="login-password"
                      aria-invalid={error ? 'true' : 'false'}
                      aria-describedby={error ? 'login-error' : undefined}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full"
                      onClick={() => setShowPassword((value) => !value)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      data-testid="login-password-toggle"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                {error && (
                  <div
                    id="login-error"
                    role="alert"
                    className="flex items-start gap-2 rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive"
                    data-testid="login-error"
                  >
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    <span>{error}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  className="h-11 w-full"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? 'Signing in…' : 'Sign in'}
                </Button>

                <p className="pt-1 text-center text-xs leading-relaxed text-muted-foreground">
                  Demo access: admin@vdrive.com / admin123
                </p>
              </form>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default LoginPage;
